/**
 * Main Application Component
 * Configures routing and provides global context
 *
 * Security Features:
 * - CSRF protection initialized on app mount
 * - All API requests automatically protected with CSRF tokens
 */

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QuizProvider } from '@context/QuizContext';
import { useCsrfInit } from './hooks/useCsrf';
import { usePageTracker } from './hooks/usePageTracker';

/**
 * ScrollToTop component - scrolls to top on route change
 */
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  usePageTracker();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

/**
 * Lazy-loaded Quiz pages
 */
const Q1Duration = React.lazy(() => import('./pages/Quiz/Q1Duration'));
const PainDurationEducation = React.lazy(() => import('./pages/Quiz/PainDurationEducation'));
const DisqualifiedTooSoon = React.lazy(() => import('./pages/Quiz/DisqualifiedTooSoon'));
const Q2Treatments = React.lazy(() => import('./pages/Quiz/Q2Treatments'));
const ConnectingMessageQ2 = React.lazy(() => import('./pages/Quiz/ConnectingMessageQ2'));
const EducationQ2A = React.lazy(() => import('./pages/Quiz/EducationQ2A'));
const EducationQ2ANone = React.lazy(() => import('./pages/Quiz/EducationQ2ANone'));
const Q3Conditions = React.lazy(() => import('./pages/Quiz/Q3Conditions'));
const DisqualifiedNonTreatable = React.lazy(() => import('./pages/Quiz/DisqualifiedNonTreatable'));
const ManualReview = React.lazy(() => import('./pages/Quiz/ManualReview'));
const PrimaryCellExplanation = React.lazy(() => import('./pages/Quiz/PrimaryCellExplanation'));
const AlternativePrimaryCellExplanation = React.lazy(() => import('./pages/Quiz/AlternativePrimaryCellExplanation'));
const Q4WhatsMissing = React.lazy(() => import('./pages/Quiz/Q4WhatsMissing'));
const Q5Urgency = React.lazy(() => import('./pages/Quiz/Q5Urgency'));
const Q6AnnualSpending = React.lazy(() => import('./pages/Quiz/Q6AnnualSpending'));
const Q7OpenQuestions = React.lazy(() => import('./pages/Quiz/Q7OpenQuestions'));
const Congratulations = React.lazy(() => import('./pages/Quiz/Congratulations'));
const CongratulationsAlternative = React.lazy(() => import('./pages/Quiz/CongratulationsAlternative'));
const Welcome = React.lazy(() => import('./pages/Quiz/Welcome'));
const TermsAndConditions = React.lazy(() => import('./pages/Quiz/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('./pages/Quiz/PrivacyPolicy'));
const ThankYouNonTreatable = React.lazy(() => import('./pages/Quiz/ThankYouNonTreatable'));
const ConversionDiagnostics = React.lazy(() => import('./pages/Diagnostics/ConversionDiagnostics'));

/**
 * Loading fallback component
 * Displayed while lazy-loaded components are being fetched
 */
const LoadingFallback: React.FC = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f7f4ed',
    }}
  >
    <div
      style={{
        textAlign: 'center',
        color: '#1d2c49',
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e5e7eb',
          borderTopColor: '#1d2c49',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px',
        }}
      />
      <p style={{ fontSize: '1.125rem', fontWeight: 500 }}>Loading...</p>
    </div>
  </div>
);

/**
 * Main App Component
 * Sets up routing, context providers, and global suspense boundaries
 */
const App: React.FC = () => {
  // Initialize CSRF protection on app mount
  useCsrfInit();

  return (
    <BrowserRouter>
      <ScrollToTop />
      <QuizProvider>
        <React.Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* New Quiz Routes */}
            <Route path="/" element={<Navigate to="/quiz/q1-duration" replace />} />
            
            {/* Q1 and Disqualification */}
            <Route path="/quiz/q1-duration" element={<Q1Duration />} />
            <Route path="/quiz/pain-duration-education" element={<PainDurationEducation />} />
            <Route path="/quiz/disqualified-too-soon" element={<DisqualifiedTooSoon />} />
            
            {/* Q2 and Educational Responses */}
            <Route path="/quiz/q2-treatments" element={<Q2Treatments />} />
            <Route path="/quiz/connecting-message-q2" element={<ConnectingMessageQ2 />} />
            <Route path="/quiz/education-q2a" element={<EducationQ2A />} />
            <Route path="/quiz/education-q2a-none" element={<EducationQ2ANone />} />
            
            {/* Q3 and Routing Pages */}
            <Route path="/quiz/q3-conditions" element={<Q3Conditions />} />
            <Route path="/quiz/disqualified-non-treatable" element={<DisqualifiedNonTreatable />} />
            <Route path="/quiz/manual-review" element={<ManualReview />} />
            
            {/* Primary Cell Explanations */}
            <Route path="/quiz/primary-cell-explanation" element={<PrimaryCellExplanation />} />
            <Route path="/quiz/alternative-primary-cell-explanation" element={<AlternativePrimaryCellExplanation />} />
            
            {/* Q4-Q7 */}
            <Route path="/quiz/q4-whats-missing" element={<Q4WhatsMissing />} />
            <Route path="/quiz/q5-urgency" element={<Q5Urgency />} />
            <Route path="/quiz/q6-annual-spending" element={<Q6AnnualSpending />} />
            <Route path="/quiz/q7-open-questions" element={<Q7OpenQuestions />} />
            
            {/* Congratulations and Welcome */}
            <Route path="/quiz/congratulations" element={<Congratulations />} />
            <Route path="/quiz/congratulations-alternative" element={<CongratulationsAlternative />} />
            <Route path="/quiz/welcome" element={<Welcome />} />
            {/* Returning visitors from the follow-up email land here (same Welcome
                component, different URL) so they don't skew the ad-funnel drop-off.
                Welcome never fires Lead or the Opt-In conversion. */}
            <Route path="/chad-video" element={<Welcome />} />
            <Route path="/quiz/thank-you-non-treatable" element={<ThankYouNonTreatable />} />
            
            {/* Legal Pages */}
            <Route path="/quiz/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/quiz/privacy-policy" element={<PrivacyPolicy />} />

            {/* Internal ops: privacy-safe conversion diagnostics (unlisted, obscured URL) */}
            <Route path="/ops-metrics-7f3a9c2e1b6d" element={<ConversionDiagnostics />} />
                        
            {/* Catch all - redirect to quiz start */}
            <Route path="*" element={<Navigate to="/quiz/q1-duration" replace />} />
          </Routes>
        </React.Suspense>
      </QuizProvider>

      {/* Global styles for spinner animation */}
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </BrowserRouter>
  );
};

export default App;
